/**
 * 认证API服务 - 与用户管理后端服务通信
 * 后端服务运行在端口9001
 */

const AUTH_API_BASE = process.env.REACT_APP_AUTH_API_BASE || 'http://localhost:9001';

// 认证相关类型定义
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  display_name: string;
  preferred_language?: 'zh' | 'ja' | 'en';
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserProfile {
  id: number;
  email: string;
  display_name: string;
  preferred_language: string;
  role: string | null;
  avatar_url: string | null;
  is_verified: boolean;
  email_verified: boolean;
  created_at: string;
  last_login_at: string | null;
  total_conversations: number | null;
  subscription_status: string;
}

export interface UserStats {
  total_conversations: number;
  total_analyses: number;
  subscription_status: string;
  remaining_credits: number;
}

export interface GoogleLoginRequest {
  credential: string;
  client_id: string;
}

/**
 * 认证API服务类
 */
export class AuthService {
  private static getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  /**
   * 用户注册
   */
  static async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '注册失败');
    }

    return response.json();
  }

  /**
   * 用户登录
   */
  static async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/auth/login-json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '登录失败');
    }

    return response.json();
  }

  /**
   * Google登录
   */
  static async googleLogin(data: GoogleLoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Google登录失败');
    }

    return response.json();
  }

  /**
   * 刷新令牌
   */
  static async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('没有刷新令牌');
    }

    const response = await fetch(`${AUTH_API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '令牌刷新失败');
    }

    return response.json();
  }

  /**
   * 获取当前用户信息
   */
  static async getCurrentUser(): Promise<UserProfile> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/auth/me`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '获取用户信息失败');
    }

    return response.json();
  }

  /**
   * 用户登出
   */
  static async logout(): Promise<void> {
    try {
      await fetch(`${AUTH_API_BASE}/api/v1/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
    } finally {
      // 清理本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  /**
   * 获取用户资料
   */
  static async getUserProfile(): Promise<UserProfile> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/users/profile`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '获取用户资料失败');
    }

    return response.json();
  }

  /**
   * 更新用户资料
   */
  static async updateUserProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/users/profile`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '更新用户资料失败');
    }

    return response.json();
  }

  /**
   * 获取用户统计信息
   */
  static async getUserStats(): Promise<UserStats> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/users/stats`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '获取用户统计失败');
    }

    return response.json();
  }

  /**
   * 获取订阅状态
   */
  static async getSubscriptionStatus(): Promise<any> {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/users/subscription-status`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '获取订阅状态失败');
    }

    return response.json();
  }

  /**
   * 检查令牌是否有效
   */
  static isTokenValid(): boolean {
    const token = localStorage.getItem('access_token');
    if (!token) return false;

    // 简单检查令牌是否存在，实际应用中可以检查过期时间
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  /**
   * 保存认证令牌
   */
  static saveTokens(authResponse: AuthResponse): void {
    localStorage.setItem('access_token', authResponse.access_token);
    localStorage.setItem('refresh_token', authResponse.refresh_token);
  }

  /**
   * 健康检查
   */
  static async healthCheck(): Promise<any> {
    const response = await fetch(`${AUTH_API_BASE}/health`);
    return response.json();
  }
} 